import React, { useState, useRef, useEffect } from 'react'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import Icon from '@forwardflow/ui/Icons'
import { Avatar, message } from 'antd'
import { AudioPlayer } from './AudioPlayer'
import { useGetRecordingPresignedUrl } from '@forwardflow/hooks/customHooks/conversation/useGetRecordingPresignedUrl'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'

dayjs.extend(duration)

interface VoiceMissedItemProps {
   item: any
   selectedLeadDetails: any
}

export const VoiceMissedItem: React.FC<VoiceMissedItemProps> = ({ item }) => {
   const { data: user } = useAuthUser({ revalidateOnMount: true })
   const [isSummaryExpanded, setIsSummaryExpanded] = useState(false)
   const [audioUrl, setAudioUrl] = useState(item?.recordingUrl || '')
   const [needsPresignedUrl, setNeedsPresignedUrl] = useState(false)
   const [isLoading, setIsLoading] = useState(false)

   const audioRef = useRef<HTMLAudioElement>(null)
   const attemptPlayRef = useRef(false)
   const recordingFileObjKey = item?.recordingFileObjKey

   const { data, mutate } = useGetRecordingPresignedUrl(
      needsPresignedUrl ? recordingFileObjKey : undefined
   )

   useEffect(() => {
      if (data?.url) {
         setAudioUrl(data.url)
         setIsLoading(false)

         if (attemptPlayRef.current && audioRef.current) {
            setTimeout(() => {
               if (audioRef.current) {
                  audioRef.current.play().catch((err) => {
                     console.error('Retry play failed:', err)
                     attemptPlayRef.current = false
                  })
               }
            }, 100)
         }
      }
   }, [data])

   useEffect(() => {
      const audio = audioRef.current
      if (!audio) return

      const handlePlay = () => {
         attemptPlayRef.current = true
      }

      const handlePause = () => {
         attemptPlayRef.current = false
      }

      audio.addEventListener('play', handlePlay)
      audio.addEventListener('pause', handlePause)

      return () => {
         audio.removeEventListener('play', handlePlay)
         audio.removeEventListener('pause', handlePause)
      }
   }, [])

   const handleAudioError = () => {
      console.log('Audio playback error - fetching presigned URL')
      if (!needsPresignedUrl) {
         setIsLoading(true)
         setNeedsPresignedUrl(true)
         mutate()
      }
   }

   const toggleExpand = () => {
      setIsSummaryExpanded(!isSummaryExpanded)
   }

   const handleCopy = () => {
      navigator.clipboard.writeText(item?.stepId).then(() => {
         message.success('Copied to clipboard')
      })
   }

   return (
      <div className="flex justify-end">
         <div className="flex flex-col items-end justify-end overflow-hidden gap-x-2 rounded-3xl rounded-br-none max-w-[800px] 3xl:w-[800px]">
            <div className="flex justify-between bg-[#944047] py-4 px-8 w-full gap-y-4 relative group">
               <div className="flex-1">
                  {user?.email === 'operator@forward-flow.com' &&
                     item?.stepId && (
                        <div className="text-[11px] text-white/70 px-0 pb-3 flex gap-x-4">
                           <span
                              className="cursor-pointer select-all"
                              title="Click to copy"
                              onClick={handleCopy}
                           >
                              Step Id:{' '}
                              <span className="text-[12px] font-medium">
                                 {item?.stepId}
                              </span>
                           </span>
                           {item?.campaignPhase && (
                              <span>
                                 Phase:{' '}
                                 <span className="text-[12px] font-medium">
                                    {item?.campaignPhase}
                                 </span>
                              </span>
                           )}
                           {item?.campaignType && (
                              <span>
                                 Type:{' '}
                                 <span className="text-[12px] font-medium">
                                    {item?.campaignType}
                                 </span>
                              </span>
                           )}
                        </div>
                     )}
                  <div className="flex">
                     <div className="h-10 px-4 py-2 rounded-full flexc gap-x-3 bg-black/20 shrink-0">
                        <Icon name="MissedCall" className="fill-white" />
                        {audioUrl && item?.data && (
                           <>
                              | <Icon name="VoiceMail" className="fill-white" />
                           </>
                        )}
                     </div>
                     <div className={`w-full h-full px-4 m-auto flexc flex-1`}>
                        {audioUrl && item?.data && (
                           <AudioPlayer
                              audioRef={audioRef}
                              audioSrc={audioUrl}
                              onError={handleAudioError}
                              isLoading={isLoading}
                           />
                        )}
                     </div>
                  </div>
               </div>

               <div className="flex items-center">
                  <div className="mr-4 text-right">
                     <p className="text-[12px] mb-1">No Answer</p>
                     <p className="text-white/70 text-[12px]">
                        {dayjs(item?.ts).format('h:mm A, D MMM YYYY')}
                     </p>
                  </div>
                  <Avatar
                     src={'/images/ff_white.png'}
                     className="w-10 h-10 p-1 shrink-0 bg-primary-dark/80"
                  >
                     {item?.assistant?.name.slice(0, 1) || 'A'}
                  </Avatar>
               </div>
            </div>

            {item?.data && audioUrl && (
               <div
                  onClick={toggleExpand}
                  className="flex items-center justify-between w-full px-8 py-5 pb-5 cursor-pointer gap-x-5 bg-white/10"
               >
                  <div
                     className={`${
                        isSummaryExpanded ? '' : 'line-clamp-1'
                     } transition-all duration-200 text-[14px]`}
                  >
                     Voicemail : {item?.data}
                  </div>
                  <Icon
                     name="arrow-down"
                     className={`${
                        isSummaryExpanded ? '-rotate-180' : ''
                     } text-white/50`}
                  />
               </div>
            )}
         </div>
      </div>
   )
}
